import React, { useEffect, useState } from 'react';
import { Box, Typography, MenuItem, TextField, Stack, FormGroup, Autocomplete, Divider, Backdrop } from '@mui/material';
import { Gender } from '../../../models/enums/gender';
import { companiesService } from '../../../services/companiesService';
import { ConfirmDialog } from '../../common/ConfirmDialog';
import { UserUtils } from '../../../helpers/userUtils';
import { Controller, useFormContext } from 'react-hook-form';
import { CompanyShortDto } from '../../../models/companies/companyShortDto';
import { FileInputFormControl } from '../../common/form/FileInputFormControl';
import { DatePickerFormControl } from '../../common/form/DatePickerFormControl';
import { TextFieldFormControl } from '../../common/form/TextFieldFormControl';
import { EditDriverFormDto } from '../../../models/drivers/editDriverFormDto';
import ImagePreview from '../../common/image/ImagePreview';
import AcceptLabel from '../../common/labels/AcceptLabel';
import DenyLabel from '../../common/labels/DenyLabel';
import TmFullButton from '../../common/buttons/TmFullButton';
import { CategoryType } from '../../../models/enums/categoryType';
import { PermissionType } from '../../../models/enums/permissionType';
import { ShowForPermission } from '../../common/permissions/ShowForPermission';
import Spinner from '../../common/Spinner';
import { usersService } from '../../../services/usersService';
import { permissionService } from '../../../services/permissionService';
import { RoleType } from '../../../models/enums/roleType';
import { snilsValidationRules } from '../../../helpers/snilsUtils';
import { DispatcherCapabilityType } from '../../../models/enums/dispatcherCapabilityType';

interface EditPersonalDataTabProps {
    setError: (message: string) => void;
    onRefresh?: () => void;
    isAdding?: boolean;
    companyId?: number;
}

export const EditPersonalDataTab: React.FC<EditPersonalDataTabProps> = ({ setError, isAdding, onRefresh, companyId }) => {
    const methods = useFormContext<EditDriverFormDto>();
    const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
    const [openEsiaConfirmDialog, setOpenEsiaConfirmDialog] = useState(false);
    const [companies, setCompanies] = useState<CompanyShortDto[]>([]);
    const [loading, setLoading] = useState(false);

    // Check if current user is Dispatcher (for field restrictions)
    const isDispatcher = permissionService.hasRole(RoleType.Dispatcher);
    const isAdmin = permissionService.hasRole(RoleType.Admin);
    const canManagePersonalData = permissionService.hasDispatcherCapability(DispatcherCapabilityType.ManageDriverPersonalData);

    // Fields should be disabled for Dispatcher role during editing (not during creation)
    const shouldDisableFields = isDispatcher && !isAdding && !isAdmin && !canManagePersonalData;

    // Dismissal logic: dispatchers can dismiss but only admins can restore
    const canDismiss = isAdmin || (isDispatcher && !methods.getValues("user.isDismissed"));
    const canRestore = permissionService.hasDispatcherCapability(DispatcherCapabilityType.RestoreDismissedDrivers);

    useEffect(() => {
        companiesService.search({
            name: companyId ? undefined : '',
            companyIds: companyId ? [companyId] : undefined,
            page: 0,
            pageSize: 999,
        })
            .then(r => setCompanies(r.data.data.items))
            .catch(e => console.log(e));
    }, [companyId]);

    const handleDismiss = (): void => {
        setLoading(true);
        setOpenConfirmDialog(false);
        const driverId = methods.getValues("id");
        const userId = methods.getValues("user.id");
        const isCurrentlyDismissed = methods.getValues("user.isDismissed");

        if (driverId && userId) {
            // Invert dismissal
            const newDismissalStatus = !isCurrentlyDismissed;

            usersService.toggleDismissal(userId, newDismissalStatus)
                .then(() => {
                    methods.setValue("user.isDismissed", newDismissalStatus);
                    if (onRefresh) {
                        onRefresh();
                    }
                })
                .catch(e => {
                    console.log(e);
                    setError(e.message);
                })
                .finally(() => setLoading(false));
        }
    }

    const handleUnlinkEsia = (): void => {
        setLoading(true);
        setOpenEsiaConfirmDialog(false);
        const userId = methods.getValues("user.id");

        if (userId) {
            usersService.unlinkEsia(userId)
                .then(() => {
                    methods.setValue("user.esiaId", undefined);
                    if (onRefresh) {
                        onRefresh();
                    }
                })
                .catch(e => {
                    console.log(e);
                    setError(e.message);
                })
                .finally(() => setLoading(false));
        }
    }

    const photoFile = methods.watch("user.photoFile");
    const photoUrl = methods.watch("user.photoUrl");

    return (
        <Box>
            <FormGroup>
                <Typography variant='h6' fontWeight="bold" mb="10px">Клиент</Typography>
                <Stack spacing={2} direction="column">
                    <Controller
                        name='companyId'
                        control={methods.control}
                        rules={{ required: 'Поле обязательно для заполнения' }}
                        render={({ field: { onChange, value } }) => (
                            <Autocomplete
                                size="small"
                                id="companyId"
                                options={companyId ? [] : companies}
                                getOptionLabel={(item) => (item?.name ?? "")}
                                value={companyId
                                    ? companies.find(company => company.id === companyId) || null
                                    : companies.find(company => company.id === value) || null} // Default to null if not found
                                onChange={(event, newValue) => {
                                    if (!companyId) { // Only allow changes if companyId is not provided
                                        onChange(newValue ? newValue.id : null);
                                    }
                                }}
                                noOptionsText={companyId ? "" : "Не найдено"}
                                isOptionEqualToValue={(option, value) => option.id === value.id}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label='Клиент'
                                        error={Boolean(methods.formState.errors.companyId)}
                                        helperText={methods.formState.errors.companyId?.message}
                                        placeholder="Введите от 3х букв"
                                        required
                                        disabled={!!companyId} // Disable if companyId is provided
                                    />
                                )}
                                renderOption={(props, option) => (
                                    <li {...props} key={option.id}>
                                        {option.name}
                                    </li>
                                )}
                            />
                        )}
                    />
                </Stack>
            </FormGroup>

            <Divider sx={{ my: 1, borderBottomWidth: 0 }} />
            {!isAdding &&
                <FormGroup>
                    <Typography variant='h6' fontWeight="bold" mb="10px">Статус</Typography>
                    <Stack spacing={2} direction="column">
                        <Box display="flex">
                            <Box style={{ width: '50%' }} pt={1}>
                                {methods.getValues("user.isDismissed")
                                    ? <DenyLabel>Уволен</DenyLabel>
                                    : <AcceptLabel>Работает</AcceptLabel>}
                            </Box>
                            <Box style={{ width: '50%', textAlign: 'end' }}>
                                <ShowForPermission catalog={CategoryType.Drivers} permission={PermissionType.Delete}>
                                    <TmFullButton
                                        onClick={() => setOpenConfirmDialog(true)}
                                        disabled={methods.getValues("user.isDismissed") ? !canRestore : !canDismiss}
                                    >
                                        {methods.getValues("user.isDismissed") ? "Восстановить" : "Уволить"}
                                    </TmFullButton>
                                </ShowForPermission>
                            </Box>
                        </Box>
                    </Stack>
                </FormGroup>
            }

            <FormGroup>
                <Typography variant='h6' fontWeight="bold" mb="10px">Личные данные</Typography>
                <Stack spacing={2} direction="column">
                    <TextFieldFormControl
                        name="user.lastName"
                        label="Фамилия"
                        required
                        disabled={shouldDisableFields}
                        transform={(value) => value.trim()}
                        validationRules={{
                            validate: (value) => {
                                if (value && value !== value.trim()) {
                                    return 'Фамилия не должна содержать пробелы в начале и конце';
                                }
                                return true;
                            }
                        }}
                    />
                    <Box display="flex">
                        <TextFieldFormControl
                            name="user.firstName"
                            label="Имя"
                            required
                            disabled={shouldDisableFields}
                            style={{ marginRight: 10 }}
                            transform={(value) => value.trim()}
                            validationRules={{
                                validate: (value) => {
                                    if (value && value !== value.trim()) {
                                        return 'Имя не должно содержать пробелы в начале и конце';
                                    }
                                    return true;
                                }
                            }}
                        />
                        <TextFieldFormControl
                            name="user.middleName"
                            label="Отчество"
                            required
                            disabled={shouldDisableFields}
                            transform={(value) => value.trim()}
                            validationRules={{
                                validate: (value) => {
                                    if (value && value !== value.trim()) {
                                        return 'Отчество не должно содержать пробелы в начале и конце';
                                    }
                                    return true;
                                }
                            }}
                        />
                    </Box>
                    <Box display="flex">
                        <Controller
                            name="user.gender"
                            control={methods.control}
                            rules={{ required: 'Поле обязательно для заполнения' }}
                            render={({ field }) => (
                                <TextField
                                    {...field}
                                    fullWidth
                                    size="small"
                                    required
                                    select
                                    disabled={shouldDisableFields}
                                    label="Пол"
                                    defaultValue=''
                                    error={Boolean(methods.formState.errors.user?.gender)}
                                    helperText={methods.formState.errors.user?.gender?.message}
                                    style={{ width: '50%', marginRight: 10 }}
                                >
                                    <MenuItem value='' disabled>Выберите пол</MenuItem>
                                    <MenuItem key={Gender.Male} value={Gender.Male}>Мужской</MenuItem>
                                    <MenuItem key={Gender.Female} value={Gender.Female}>Женский</MenuItem>
                                </TextField>
                            )}
                        />

                        <DatePickerFormControl
                            name="user.birthDate"
                            label={"Дата рождения"}
                            style={{ width: '50%' }}
                            required
                        />
                    </Box>
                </Stack>
            </FormGroup>

            {!isAdding && methods.getValues("user.esiaId") &&
                <FormGroup>
                    <Typography variant='h6' fontWeight="bold" mb={2} mt={2}>Госуслуги</Typography>
                    <Stack spacing={2} direction="column">
                        <Box display="flex">
                            <Box style={{ width: '50%' }} pt={1}>
                                <AcceptLabel>Привязан</AcceptLabel>
                            </Box>
                            <Box style={{ width: '50%', textAlign: 'end' }}>
                                <ShowForPermission catalog={CategoryType.Drivers} permission={PermissionType.Edit}>
                                    <TmFullButton
                                        onClick={() => setOpenEsiaConfirmDialog(true)}
                                        disabled={!(methods.getValues("id") && methods.getValues("user.esiaId"))}
                                    >
                                        Отвязать
                                    </TmFullButton>
                                </ShowForPermission>
                            </Box>
                        </Box>
                    </Stack>
                </FormGroup>
            }

            <Divider sx={{ my: 1, borderBottomWidth: 0 }} />
            <FormGroup>
                <Typography variant='h6' fontWeight="bold" mb={0.5}>Документы</Typography>
                <Stack spacing={0} direction="column">
                    <Typography fontWeight="bold" mb={2}>Водительское удостоверение</Typography>
                    <Box display="flex" sx={{ mb: 2 }}>
                        <TextFieldFormControl
                            name="licenceSerial"
                            label="Серия"
                            style={{ width: '50%', marginRight: 10 }}
                        />
                        <TextFieldFormControl
                            name="licenceNumber"
                            label="Номер"
                            style={{ width: '50%' }}
                        />
                    </Box>
                    <Box display="flex" sx={{ mb: 2 }}>
                        <DatePickerFormControl
                            name="licenceIssued"
                            label={"Дата выдачи"}
                            style={{ width: '50%', marginRight: 10 }}
                        />
                        <DatePickerFormControl
                            name="licenceExpired"
                            label={"Действительно до"}
                            style={{ width: '50%' }}
                        />
                    </Box>
                    <Typography fontWeight="bold" mb={2}>Снилс</Typography>
                    <TextFieldFormControl
                        name="snils"
                        label="СНИЛС"
                        mask="999-999-999 99"
                        placeholder="123-456-789 00"
                        style={{ marginBottom: 10 }}
                        validationRules={snilsValidationRules}
                    />
                    <Typography fontWeight="bold" mb={2}>Паспорт</Typography>
                    <Box display="flex">
                        <TextFieldFormControl
                            name="passportSerial"
                            label="Серия"
                            style={{ marginRight: 10 }}
                        />
                        <TextFieldFormControl
                            name="passportNumber"
                            label="Номер"
                        />
                    </Box>
                </Stack>
            </FormGroup>
            <Divider sx={{ my: 1, borderBottomWidth: 0 }} />
            <Typography variant='h6' fontWeight="bold" mb={2}>Служебная информация</Typography>
            <Stack spacing={2} direction="column">
                <TextFieldFormControl
                    name="employeeNumber"
                    label="Табельный номер"
                    required
                    disabled={shouldDisableFields}
                    style={{ marginBottom: 10 }}
                    transform={(value) => value.replace(/\D/g, '')}
                    validationRules={{
                        pattern: {
                            value: /^\d+$/,
                            message: 'Табельный номер должен содержать только цифры'
                        }
                    }}
                />
            </Stack>

            <Typography variant='h6' fontWeight="bold" mb={0.5}>Фото водителя</Typography>
            <Stack spacing={2} direction="column">
                <FileInputFormControl name="user.photoFile" />
                {(photoFile || photoUrl) && (
                    <ImagePreview
                        file={photoFile}
                        url={photoUrl}
                        altText="Driver Photo"
                        onRemove={() => {
                            methods.setValue("user.photoFile", undefined);
                            methods.setValue("user.photoUrl", undefined);
                        }}
                    />
                )}
            </Stack>
            <ConfirmDialog
                open={openConfirmDialog}
                title={methods.getValues("user.isDismissed") ? "Восстановление водителя" : "Увольнение водителя"}
                content={
                    <>
                        {methods.getValues("user.isDismissed")
                            ? "Вы уверены, что хотите восстановить водителя"
                            : "Вы уверены, что хотите уволить водителя"
                        }
                        <Typography variant="body1" align="center" fontWeight="bold">
                            {UserUtils.getFio(methods.getValues("user"))}
                        </Typography>
                    </>
                }
                onClose={() => setOpenConfirmDialog(false)}
                onConfirm={handleDismiss}
            />
            <ConfirmDialog
                open={openEsiaConfirmDialog}
                title="Отвязка от Госуслуг"
                content={
                    <>
                        Вы уверены, что хотите отвязать водителя от Госуслуг
                        <Typography variant="body1" align="center" fontWeight="bold">
                            {UserUtils.getFio(methods.getValues("user"))}
                        </Typography>
                    </>
                }
                onClose={() => setOpenEsiaConfirmDialog(false)}
                onConfirm={handleUnlinkEsia}
            />
            <Backdrop
                sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
                open={loading}
            >
                <Spinner />
            </Backdrop>
        </Box>
    );
};
